// src/components/AdminView.jsx
// Admin settings view for branding, templates, team management, and status configuration

import React, { useState, useEffect, useMemo } from 'react';
import {
  Settings, UserPlus, FileText, ArrowLeft, Plus, Trash2, GripVertical, Loader2, X, LogOut,
  Upload, CheckCircle2, AlertCircle, RotateCcw, Trash, Flame, Check,
  Palette, Layers, CheckSquare, Copy, Folder, Users, MapPin, FolderArchive, Mail, Activity, Eye,
  ClipboardList, ChevronUp, ChevronDown, EyeOff, Save, KeyRound, Briefcase
} from 'lucide-react';
import { useOrganization, useTeam, useDealStages, useTaskStatuses, useDeletedDeals, useAuditLog, useDealReasonCategories } from '../hooks/useSupabase';
import { useDisplayColors } from '../lib/ThemeContext';
import { supabase, geocodeAddress as geocodeAddressShared } from '../lib/supabase';
import LfgLocationsTab from './LfgLocationsTab';
import DigestSettingsTab from './DigestSettingsTab';
import VendorFoldersTab from './VendorFoldersTab';
import FileCategoriesTab from './FileCategoriesTab';

// LFG Brand Colors (defaults)
const LFG_COLORS = {
  navy: '#1A3665',
  gray: '#707578',
  green: '#85BD66',
};

// Fallback when stages haven't loaded — chronological cycle + On Hold +
// two terminals (Closed, Canceled).
const DEFAULT_PHASES = [
  { id: 'future-target', name: 'Future Target', color: '#94A3B8' },
  { id: 'prospect',      name: 'Prospect',      color: '#6B7280' },
  { id: 'diligence',     name: 'Diligence',     color: '#EAB308' },
  { id: 'offer-made',    name: 'Offer Made',    color: '#F97316' },
  { id: 'under-loi',     name: 'Under LOI',     color: '#3B82F6' },
  { id: 'on-hold',       name: 'On Hold',       color: '#A855F7' },
  { id: 'closed',        name: 'Closed',        color: '#22C55E', is_terminal: true },
  { id: 'canceled',      name: 'Canceled',      color: '#EF4444', is_terminal: true },
];

export default function AdminView({
  organization, pc, sc, adminTab, setAdminTab, setView,
  newTask, setNewTask, showToast, signOut,
  isAdmin = false,
  readOnly = false,
}) {
  const { 
    organization: orgData, templates, loading,
    updateOrganization, addTemplate, updateTemplate, deleteTemplate 
  } = useOrganization(organization?.id);
  
  const { team, invites, inviteMember, cancelInvite, resendInvite, updateMemberRole, removeMember, sendPasswordReset } = useTeam(organization?.id);
  const [resendingInviteId, setResendingInviteId] = useState(null);
  const { stages, loading: stagesLoading, addStage, updateStage, deleteStage } = useDealStages(organization?.id);
  const { statuses, loading: statusesLoading, addStatus, updateStatus, deleteStatus } = useTaskStatuses(organization?.id);
  const { cancellationCategories, delayCategories, loading: reasonCategoriesLoading, addCategory: addReasonCategory, deactivateCategory: deactivateReasonCategory } = useDealReasonCategories(organization?.id);
  const [newReasonLabel, setNewReasonLabel] = useState({ cancellation: '', delay: '' });
  const { logs: auditLogs, loading: auditLoading } = useAuditLog(organization?.id);
  
  // Use database stages or fallback to defaults
  const PHASES = stages?.length > 0 ? stages : DEFAULT_PHASES;

  const [newMember, setNewMember] = useState({ name: '', email: '', role: 'member' });
  const [teamRoleFilter, setTeamRoleFilter] = useState('all');
  const [teamSearch, setTeamSearch] = useState('');
  const [manageAccessFor, setManageAccessFor] = useState(null); // deal_advisor team-member row
  const [newStage, setNewStage] = useState({ name: '', color: '#6b7280', is_terminal: false });
  const [editingStage, setEditingStage] = useState(null); // { id, name, color, is_terminal }
  const [deleteStageModal, setDeleteStageModal] = useState(null); // { stage, dealCount, taskCount, replacement: '' }
  const [deletingStage, setDeletingStage] = useState(false);
  const [stageDealCounts, setStageDealCounts] = useState({}); // { stageId: count }
  const [newStatus, setNewStatus] = useState({ name: '', color: '#6b7280', is_complete: false });
  const [editingTemplate, setEditingTemplate] = useState(null); // { id, name, phase, is_doc_request, relative_days, base_date, category_id }
  const [fileCategories, setFileCategories] = useState([]);

  // Build nested category options for dropdown (must be after fileCategories state)
  const categoryOptions = (() => {
    const roots = fileCategories.filter(c => !c.parent_id);
    const options = [];
    roots.forEach(root => {
      options.push({ id: root.id, label: root.display_name, indent: 0 });
      const children = fileCategories.filter(c => c.parent_id === root.id);
      children.forEach(child => {
        options.push({ id: child.id, label: root.display_name + ' \u2192 ' + child.display_name, indent: 1 });
      });
    });
    return options;
  })();
  
  // Fetch file categories for template category dropdown
  useEffect(() => {
    if (!organization?.id) return;
    const fetchCategories = async () => {
      const { data } = await supabase
        .from('file_categories')
        .select('id, display_name, sort_order, parent_id')
        .eq('organization_id', organization.id)
        .eq('is_active', true)
        .order('sort_order');
      setFileCategories(data || []);
    };
    fetchCategories();
  }, [organization?.id]);
  const [brandingForm, setBrandingForm] = useState({
    name: organization?.name || 'Landmark Legacy Room',
    logo_url: organization?.logo_url || '',
    primary_color: organization?.primary_color || LFG_COLORS.navy,
    secondary_color: organization?.secondary_color || LFG_COLORS.gray
  });

  useEffect(() => {
    if (orgData) {
      setBrandingForm({
        name: orgData.name || 'Landmark Legacy Room',
        logo_url: orgData.logo_url || '',
        primary_color: orgData.primary_color || LFG_COLORS.navy,
        secondary_color: orgData.secondary_color || LFG_COLORS.gray
      });
    }
  }, [orgData]);

  // Which sign-in method(s) this org allows, and which email domains
  // auto-provision a new SSO user as 'member'. authForm.domainsText is the
  // comma-separated editable form; allowed_email_domains itself stays an
  // array on save.
  const [authForm, setAuthForm] = useState({
    sso_enabled: organization?.sso_enabled ?? true,
    password_auth_enabled: organization?.password_auth_enabled ?? true,
    domainsText: (organization?.allowed_email_domains || []).join(', '),
  });

  useEffect(() => {
    if (orgData) {
      setAuthForm({
        sso_enabled: orgData.sso_enabled ?? true,
        password_auth_enabled: orgData.password_auth_enabled ?? true,
        domainsText: (orgData.allowed_email_domains || []).join(', '),
      });
    }
  }, [orgData]);

  const handleSaveAuthentication = async () => {
    if (!authForm.sso_enabled && !authForm.password_auth_enabled) {
      showToast('At least one sign-in method must stay enabled', 'error');
      return;
    }
    try {
      const allowed_email_domains = authForm.domainsText
        .split(',')
        .map(d => d.trim().toLowerCase())
        .filter(Boolean);
      await updateOrganization({
        sso_enabled: authForm.sso_enabled,
        password_auth_enabled: authForm.password_auth_enabled,
        allowed_email_domains,
      });
      showToast('Authentication settings updated');
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  // Fetch deal counts per stage for admin display
  useEffect(() => {
    if (!organization?.id) return;
    const fetchStageCounts = async () => {
      const { data } = await supabase
        .from('deals')
        .select('stage')
        .eq('organization_id', organization.id)
        .is('deleted_at', null);
      const counts = {};
      (data || []).forEach(d => { counts[d.stage] = (counts[d.stage] || 0) + 1; });
      setStageDealCounts(counts);
    };
    fetchStageCounts();
  }, [organization?.id, stages]);

  const handleSaveBranding = async () => {
    try {
      await updateOrganization(brandingForm);
      showToast('Branding updated');
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  // Save inline stage edits (name, color, is_terminal)
  const handleSaveStage = async (stageId, updates) => {
    try {
      await updateStage(stageId, updates);
      setEditingStage(null);
      showToast('Stage updated');
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  // Initiate stage deletion — count affected deals/tasks/templates first
  const handleInitDeleteStage = async (stage) => {
    try {
      const [{ count: dealCount }, { count: taskCount }, { count: templateCount }] = await Promise.all([
        supabase.from('deals').select('id', { count: 'exact', head: true })
          .eq('organization_id', organization.id).eq('stage', stage.id).is('deleted_at', null),
        supabase.from('tasks').select('id', { count: 'exact', head: true })
          .eq('organization_id', organization.id).eq('phase', stage.id),
        supabase.from('task_templates').select('id', { count: 'exact', head: true })
          .eq('organization_id', organization.id).eq('phase', stage.id),
      ]);
      setDeleteStageModal({ stage, dealCount: dealCount || 0, taskCount: taskCount || 0, templateCount: templateCount || 0, replacement: '' });
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  // Execute cascading delete: reassign deals/tasks/templates, then delete stage
  const handleConfirmDeleteStage = async () => {
    const { stage, replacement, dealCount, taskCount, templateCount } = deleteStageModal;
    const hasData = dealCount > 0 || taskCount > 0 || templateCount > 0;
    if (hasData && !replacement) return;
    setDeletingStage(true);
    try {
      // Reassign deals, tasks, and templates if there are any
      if (dealCount > 0) {
        const { error: dealErr } = await supabase.from('deals')
          .update({ stage: replacement })
          .eq('organization_id', organization.id).eq('stage', stage.id).is('deleted_at', null);
        if (dealErr) throw dealErr;
      }
      if (taskCount > 0) {
        const { error: taskErr } = await supabase.from('tasks')
          .update({ phase: replacement })
          .eq('organization_id', organization.id).eq('phase', stage.id);
        if (taskErr) throw taskErr;
      }
      if (templateCount > 0) {
        const { error: tmplErr } = await supabase.from('task_templates')
          .update({ phase: replacement })
          .eq('organization_id', organization.id).eq('phase', stage.id);
        if (tmplErr) throw tmplErr;
      }
      await deleteStage(stage.id);
      // Refresh counts
      const { data } = await supabase.from('deals').select('stage')
        .eq('organization_id', organization.id).is('deleted_at', null);
      const counts = {};
      (data || []).forEach(d => { counts[d.stage] = (counts[d.stage] || 0) + 1; });
      setStageDealCounts(counts);
      setDeleteStageModal(null);
      const totalReassigned = dealCount + taskCount + templateCount;
      showToast(`Stage "${stage.name}" deleted${totalReassigned > 0 ? ` — ${dealCount} deal(s), ${taskCount} task(s), ${templateCount} template(s) reassigned` : ''}`);
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setDeletingStage(false);
    }
  };

  const handleAddTemplate = async () => {
    if (!newTask.name.trim() || !newTask.phase) return;
    try {
      await addTemplate({
        name: newTask.name,
        phase: newTask.phase,
        is_doc_request: newTask.is_doc_request,
        relative_days: newTask.relative_days,
        base_date: newTask.base_date
      });
      setNewTask({ phase: '', name: '', is_doc_request: false, relative_days: 0, base_date: 'loi' });
      showToast('Template added');
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (!confirm('Delete this template?')) return;
    try {
      await deleteTemplate(id);
      showToast('Template deleted');
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleInviteMember = async () => {
    if (!newMember.name || !newMember.email) return;
    try {
      await inviteMember(newMember.email, newMember.name, newMember.role);
      setNewMember({ name: '', email: '', role: 'member' });
      showToast('Team member added');
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  // Use provided colors or fall back to LFG defaults
  const primaryColor = pc || LFG_COLORS.navy;
  const secondaryColor = sc || LFG_COLORS.gray;
  const { displayPrimary, displaySecondary, displayNavy, displayGreen } = useDisplayColors(pc, sc);

  // Tabs marked memberOk are operational tools members + admins both need
  // (importing deals, viewing activity, restoring from trash). The rest are
  // org-configuration tabs that stay admin-only.
  const ADMIN_TABS_ALL = [
    { key: 'branding',        label: 'Branding',        icon: Palette },
    { key: 'authentication',  label: 'Authentication',  icon: KeyRound },
    { key: 'deal-stages',     label: 'Deal Stages',     icon: Layers },
    { key: 'task-statuses',   label: 'Task Statuses',   icon: CheckSquare },
    { key: 'deal-reasons',    label: 'Deal Reasons',    icon: AlertCircle },
    { key: 'templates',       label: 'Task Templates',  icon: Copy },
    { key: 'file-categories', label: 'File Categories', icon: Folder },
    { key: 'team',            label: 'Team Members',    icon: Users },
    { key: 'locations',       label: 'LFG Locations',   icon: MapPin },
    { key: 'vendor-folders',  label: 'Vendor Folders',  icon: FolderArchive },
    { key: 'digest',          label: 'Daily Digest',    icon: Mail },
    { key: 'surveys',         label: 'Surveys',         icon: ClipboardList },
    { key: 'import',          label: 'Import Deals',    icon: Upload,   memberOk: true },
    { key: 'activity',        label: 'Activity Log',    icon: Activity, memberOk: true },
    { key: 'trash',           label: 'Trash',           icon: Trash2,   memberOk: true },
  ];
  // Member role sees only the operational tools; admin (and board_member in
  // read-only mode) sees everything.
  const isMemberOnly = !isAdmin && !readOnly;
  const ADMIN_TABS = isMemberOnly ? ADMIN_TABS_ALL.filter(t => t.memberOk) : ADMIN_TABS_ALL;
  // If the current tab isn't in the role's allowed set, snap to the first
  // visible tab (avoids rendering nothing if a member arrives with adminTab
  // pointing at a config-only tab).
  useEffect(() => {
    if (!ADMIN_TABS.some(t => t.key === adminTab)) {
      setAdminTab(ADMIN_TABS[0]?.key || 'import');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminTab, isMemberOnly]);
  const activeTabMeta = ADMIN_TABS.find(t => t.key === adminTab) || ADMIN_TABS[0];

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-8 pb-12">
      {/* Read-only banner for board members. The CSS rules below disable
          every button (except those tagged .ro-allow), input, select, and
          textarea inside .admin-readonly so a board member can browse
          settings without being able to mutate anything. Tab navigation
          is allowed because tab buttons get .ro-allow added. */}
      {readOnly && (
        <>
          <style>{`
            .admin-readonly button:not(.ro-allow),
            .admin-readonly input:not(.ro-allow),
            .admin-readonly select:not(.ro-allow),
            .admin-readonly textarea:not(.ro-allow),
            .admin-readonly [contenteditable] {
              pointer-events: none !important;
              opacity: 0.55 !important;
              cursor: not-allowed !important;
            }
            .admin-readonly label { cursor: default !important; }
          `}</style>
          <div
            className="mb-4 rounded-xl px-4 py-3 flex items-center gap-3"
            style={{ backgroundColor: '#FEF3C7', border: '1px solid #FDE68A', color: '#92400E' }}
          >
            <Eye className="w-4 h-4 flex-shrink-0" />
            <div className="text-[13px]">
              <span className="font-semibold">Read-only access.</span>{' '}
              Board members can browse every settings tab but cannot save changes here. To edit settings, contact an admin.
            </div>
          </div>
        </>
      )}
      {/* Page heading */}
      <div className="mb-6">
        <h1
          className="text-2xl font-semibold tracking-tight"
          style={{
            background: 'linear-gradient(135deg, #0F172A 0%, #1A3665 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            letterSpacing: '-0.02em',
          }}
        >
          Settings
        </h1>
        <p className="text-sm mt-1" style={{ color: '#475569' }}>
          Branding, templates, team, and organization configuration
        </p>
      </div>

      <div className="flex gap-5 items-start">
        {/* Secondary sidebar — admin section list */}
        <aside
          className="w-56 flex-shrink-0 rounded-xl overflow-hidden sticky top-4"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            border: '1px solid #E5E9F0',
            backdropFilter: 'blur(6px)',
            boxShadow: '0 1px 2px rgba(15, 23, 42, 0.03)',
          }}
        >
          <div className="px-3 py-3 space-y-0.5">
            <div className="text-[10px] font-semibold uppercase tracking-[0.1em] px-2 pt-1 pb-1.5" style={{ color: '#94A3B8' }}>
              Configure
            </div>
            {ADMIN_TABS.map(t => {
              const Icon = t.icon;
              const isActive = adminTab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setAdminTab(t.key)}
                  className="ro-allow w-full h-9 flex items-center gap-2.5 px-3 text-[13px] font-medium rounded-lg transition-all text-left"
                  style={isActive ? {
                    background: `linear-gradient(135deg, ${primaryColor} 0%, #2E4B7C 100%)`,
                    color: 'white',
                    boxShadow: `0 1px 2px ${primaryColor}55, 0 2px 4px ${primaryColor}22`,
                  } : {
                    backgroundColor: 'transparent',
                    color: '#475569',
                  }}
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.backgroundColor = 'rgba(15, 23, 42, 0.04)'; }}
                  onMouseLeave={e => { if (!isActive) e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  {Icon && <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={2.2} />}
                  <span className="truncate">{t.label}</span>
                </button>
              );
            })}
          </div>
        </aside>

        {/* Content card. Wrapped in admin-readonly when readOnly so the
            CSS rules above neutralize all input/button/select clicks while
            still letting the user scroll, hover, and read everything. */}
        <div
          className={`flex-1 min-w-0 rounded-2xl overflow-hidden${readOnly ? ' admin-readonly' : ''}`}
          style={{
            backgroundColor: '#ffffff',
            border: '1px solid #E5E9F0',
            boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 4px 12px rgba(15, 23, 42, 0.03)',
          }}
        >
          {/* Tab header — icon badge + title so each admin section feels identifiable */}
          <div className="px-6 py-5 flex items-center gap-3" style={{ borderBottom: '1px solid #F0F2F6' }}>
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{
                background: `linear-gradient(135deg, ${primaryColor} 0%, #2E4B7C 100%)`,
                boxShadow: `0 2px 6px ${primaryColor}40`,
              }}
            >
              {activeTabMeta.icon && <activeTabMeta.icon className="w-4 h-4 text-white" strokeWidth={2.2} />}
            </div>
            <h2 className="text-base font-semibold tracking-tight" style={{ color: '#0F172A', letterSpacing: '-0.01em' }}>
              {activeTabMeta.label}
            </h2>
          </div>

          <div className="p-6">
          {/* Branding Tab */}
          {adminTab === 'branding' && (
            <div className="space-y-4">
              <h2 className="font-semibold text-lg" style={{ color: displayPrimary }}>Branding</h2>
              <div>
                <label className="block text-sm mb-1 font-medium">Company Name</label>
                <input 
                  value={brandingForm.name} 
                  onChange={e => setBrandingForm(p => ({ ...p, name: e.target.value }))}
                  className="w-full border rounded px-3 py-2 focus:ring-2 focus:outline-none" 
                  placeholder="Landmark Legacy Room"
                />
              </div>
              <div>
                <label className="block text-sm mb-1 font-medium">Logo URL</label>
                <input 
                  value={brandingForm.logo_url} 
                  onChange={e => setBrandingForm(p => ({ ...p, logo_url: e.target.value }))}
                  placeholder="https://..." 
                  className="w-full border rounded px-3 py-2 focus:ring-2 focus:outline-none" 
                />
                <p className="text-xs mt-1" style={{ color: displaySecondary }}>
                  Tip: For Google Drive images, use format: https://drive.google.com/uc?export=view&id=YOUR_FILE_ID
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-1 font-medium">Primary Color</label>
                  <div className="flex gap-2">
                    <input 
                      type="color" 
                      value={brandingForm.primary_color} 
                      onChange={e => setBrandingForm(p => ({ ...p, primary_color: e.target.value }))}
                      className="w-12 h-10 border rounded cursor-pointer" 
                    />
                    <input 
                      type="text" 
                      value={brandingForm.primary_color} 
                      onChange={e => setBrandingForm(p => ({ ...p, primary_color: e.target.value }))}
                      className="flex-1 border rounded px-3 py-2 font-mono text-sm" 
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm mb-1 font-medium">Secondary Color</label>
                  <div className="flex gap-2">
                    <input 
                      type="color" 
                      value={brandingForm.secondary_color} 
                      onChange={e => setBrandingForm(p => ({ ...p, secondary_color: e.target.value }))}
                      className="w-12 h-10 border rounded cursor-pointer" 
                    />
                    <input 
                      type="text" 
                      value={brandingForm.secondary_color} 
                      onChange={e => setBrandingForm(p => ({ ...p, secondary_color: e.target.value }))}
                      className="flex-1 border rounded px-3 py-2 font-mono text-sm" 
                    />
                  </div>
                </div>
              </div>
              
              {/* Preview */}
              <div className="mt-4">
                <label className="block text-sm mb-2 font-medium">Preview</label>
                <div 
                  className="p-4 rounded text-white font-semibold" 
                  style={{ background: `linear-gradient(135deg, ${brandingForm.primary_color}, ${brandingForm.secondary_color})` }}
                >
                  {brandingForm.name || 'Company Name'}
                </div>
              </div>
              
              <button
                onClick={handleSaveBranding}
                className="px-4 py-2 text-white rounded hover:opacity-90 transition-opacity"
                style={{ backgroundColor: primaryColor }}
              >
                Save Changes
              </button>
            </div>
          )}

          {/* Authentication Tab */}
          {adminTab === 'authentication' && (
            <div className="space-y-4">
              <h2 className="font-semibold text-lg" style={{ color: displayPrimary }}>Authentication</h2>
              <p className="text-sm" style={{ color: displaySecondary }}>
                Choose which sign-in method(s) your team uses. Both can be on at once — e.g. staff sign in with Microsoft 365 while outside counsel/accountants still use the email-and-password link from their invite.
              </p>

              <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={authForm.sso_enabled}
                  onChange={e => setAuthForm(p => ({ ...p, sso_enabled: e.target.checked }))}
                />
                <div>
                  <div className="font-medium text-sm">Microsoft 365 sign-in</div>
                  <div className="text-xs" style={{ color: displaySecondary }}>
                    Staff sign in with their work Microsoft account. New sign-ins from an allowed domain below are auto-added as team members.
                  </div>
                </div>
              </label>

              {authForm.sso_enabled && (
                <div className="ml-8">
                  <label className="block text-sm mb-1 font-medium">Allowed email domains</label>
                  <input
                    value={authForm.domainsText}
                    onChange={e => setAuthForm(p => ({ ...p, domainsText: e.target.value }))}
                    placeholder="yourcompany.com, yourcompany.onmicrosoft.com"
                    className="w-full border rounded px-3 py-2 focus:ring-2 focus:outline-none"
                  />
                  <p className="text-xs mt-1" style={{ color: displaySecondary }}>
                    Comma-separated. Only affects auto-provisioning on first sign-in — doesn't restrict who can be invited.
                  </p>
                </div>
              )}

              <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={authForm.password_auth_enabled}
                  onChange={e => setAuthForm(p => ({ ...p, password_auth_enabled: e.target.checked }))}
                />
                <div>
                  <div className="font-medium text-sm">Email &amp; password sign-in</div>
                  <div className="text-xs" style={{ color: displaySecondary }}>
                    Anyone invited from the Team Members tab (including admins/members, not just external contacts) sets a password from their invite link.
                  </div>
                </div>
              </label>

              <button
                onClick={handleSaveAuthentication}
                className="px-4 py-2 text-white rounded hover:opacity-90 transition-opacity"
                style={{ backgroundColor: primaryColor }}
              >
                Save Changes
              </button>
            </div>
          )}

          {/* Deal Stages Tab */}
          {adminTab === 'deal-stages' && (
            <div>
              <h2 className="font-semibold text-lg mb-2" style={{ color: displayPrimary }}>Deal Stages</h2>
              <p className="text-sm mb-4" style={{ color: displaySecondary }}>
                Configure the stages a deal can be in. Click a stage to edit its name, color, or terminal flag. Terminal stages indicate the deal has ended. Deleting a stage with existing deals will require reassigning them first.
              </p>

              {stagesLoading ? (
                <div className="flex items-center gap-2 py-4">
                  <Loader2 className="w-4 h-4 animate-spin" style={{ color: displayPrimary }} />
                  <span style={{ color: displaySecondary }}>Loading stages...</span>
                </div>
              ) : (
                <>
                  <div className="border rounded mb-4 divide-y">
                    {stages.map(stage => {
                      const isEditing = editingStage?.id === stage.id;
                      const dealCount = stageDealCounts[stage.id] || 0;
                      return (
                        <div key={stage.id} className="hover:bg-gray-50">
                          {isEditing ? (
                            /* Inline edit mode */
                            <div className="p-3 bg-blue-50 space-y-2">
                              <div className="flex items-center gap-2">
                                <input
                                  type="color"
                                  value={editingStage.color}
                                  onChange={e => setEditingStage(p => ({ ...p, color: e.target.value }))}
                                  className="w-8 h-8 border rounded cursor-pointer flex-shrink-0"
                                />
                                <input
                                  value={editingStage.name}
                                  onChange={e => setEditingStage(p => ({ ...p, name: e.target.value }))}
                                  className="flex-1 border rounded px-3 py-1.5 text-sm font-medium"
                                  autoFocus
                                  onKeyDown={e => { if (e.key === 'Escape') setEditingStage(null); }}
                                />
                                <label className="flex items-center gap-1.5 text-sm whitespace-nowrap">
                                  <input
                                    type="checkbox"
                                    checked={editingStage.is_terminal}
                                    onChange={e => setEditingStage(p => ({ ...p, is_terminal: e.target.checked }))}
                                    className="rounded"
                                  />
                                  Terminal
                                </label>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-xs" style={{ color: displaySecondary }}>ID: {stage.id}</span>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => setEditingStage(null)}
                                    className="px-3 py-1 text-xs border rounded hover:bg-gray-100"
                                  >Cancel</button>
                                  <button
                                    onClick={() => handleSaveStage(stage.id, {
                                      name: editingStage.name,
                                      color: editingStage.color,
                                      is_terminal: editingStage.is_terminal
                                    })}
                                    disabled={!editingStage.name.trim()}
                                    className="px-3 py-1 text-xs text-white rounded hover:opacity-90 disabled:opacity-50"
                                    style={{ backgroundColor: primaryColor }}
                                  >
                                    <Check className="w-3 h-3 inline mr-1" />Save
                                  </button>
                                </div>
                              </div>
                            </div>
                          ) : (
                            /* Display mode */
                            <div className="flex items-center gap-3 p-3">
                              <div
                                className="w-4 h-4 rounded flex-shrink-0"
                                style={{ backgroundColor: stage.color }}
                              />
                              <button
                                className="flex-1 text-left font-medium hover:underline"
                                onClick={() => setEditingStage({ id: stage.id, name: stage.name, color: stage.color, is_terminal: stage.is_terminal })}
                                title="Click to edit"
                              >
                                {stage.name}
                              </button>
                              {stage.is_terminal && (
                                <span className="text-xs px-2 py-0.5 bg-gray-100 rounded">Terminal</span>
                              )}
                              <span className="text-xs px-2 py-0.5 rounded" style={{ color: displaySecondary, backgroundColor: dealCount > 0 ? '#f3f4f6' : 'transparent' }}>
                                {dealCount} deal{dealCount !== 1 ? 's' : ''}
                              </span>
                              <button
                                onClick={() => handleInitDeleteStage(stage)}
                                className="p-1 hover:bg-red-50 rounded text-red-400 hover:text-red-600 transition-colors"
                                title="Delete stage"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="bg-gray-50 rounded p-4">
                    <h3 className="font-medium mb-3">Add New Stage</h3>
                    <div className="flex gap-2 items-end">
                      <div className="flex-1">
                        <label className="block text-xs mb-1">Name</label>
                        <input 
                          value={newStage.name}
                          onChange={e => setNewStage(p => ({ ...p, name: e.target.value }))}
                          placeholder="e.g., Under Review"
                          className="w-full border rounded px-3 py-2"
                        />
                      </div>
                      <div>
                        <label className="block text-xs mb-1">Color</label>
                        <input 
                          type="color"
                          value={newStage.color}
                          onChange={e => setNewStage(p => ({ ...p, color: e.target.value }))}
                          className="w-12 h-10 border rounded cursor-pointer"
                        />
                      </div>
                      <label className="flex items-center gap-2 pb-2">
                        <input 
                          type="checkbox"
                          checked={newStage.is_terminal}
                          onChange={e => setNewStage(p => ({ ...p, is_terminal: e.target.checked }))}
                          className="rounded"
                        />
                        <span className="text-sm">Terminal</span>
                      </label>
                      <button
                        onClick={async () => {
                          if (!newStage.name.trim()) return;
                          try {
                            await addStage(newStage);
                            setNewStage({ name: '', color: '#6b7280', is_terminal: false });
                            showToast('Stage added');
                          } catch (err) {
                            showToast(err.message, 'error');
                          }
                        }}
                        className="px-4 py-2 text-white rounded hover:opacity-90 transition-opacity"
                        style={{ backgroundColor: primaryColor }}
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </>
              )}

              {/* Delete Stage Confirmation Modal */}
              {deleteStageModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                    <h3 className="font-semibold text-lg mb-1" style={{ color: displayPrimary }}>Delete Stage</h3>
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-3 h-3 rounded" style={{ backgroundColor: deleteStageModal.stage.color }} />
                      <span className="font-medium">{deleteStageModal.stage.name}</span>
                    </div>

                    {(deleteStageModal.dealCount > 0 || deleteStageModal.taskCount > 0 || deleteStageModal.templateCount > 0) ? (
                      <>
                        <div className="bg-amber-50 border border-amber-200 rounded p-3 mb-4">
                          <div className="flex items-start gap-2">
                            <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                            <div className="text-sm text-amber-800">
                              <p className="font-medium mb-1">This stage is in use:</p>
                              {deleteStageModal.dealCount > 0 && (
                                <p>{deleteStageModal.dealCount} deal{deleteStageModal.dealCount !== 1 ? 's' : ''} currently in this stage</p>
                              )}
                              {deleteStageModal.taskCount > 0 && (
                                <p>{deleteStageModal.taskCount} task{deleteStageModal.taskCount !== 1 ? 's' : ''} assigned to this phase</p>
                              )}
                              {deleteStageModal.templateCount > 0 && (
                                <p>{deleteStageModal.templateCount} task template{deleteStageModal.templateCount !== 1 ? 's' : ''} in this phase</p>
                              )}
                              <p className="mt-1">Select a replacement stage before deleting.</p>
                            </div>
                          </div>
                        </div>
                        <div className="mb-4">
                          <label className="block text-sm font-medium mb-1">Reassign to:</label>
                          <select
                            value={deleteStageModal.replacement}
                            onChange={e => setDeleteStageModal(p => ({ ...p, replacement: e.target.value }))}
                            className="w-full border rounded px-3 py-2"
                          >
                            <option value="">Select a stage...</option>
                            {stages.filter(s => s.id !== deleteStageModal.stage.id).map(s => (
                              <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                          </select>
                        </div>
                      </>
                    ) : (
                      <p className="text-sm mb-4" style={{ color: displaySecondary }}>
                        No deals or tasks are using this stage. It can be safely deleted.
                      </p>
                    )}

                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => setDeleteStageModal(null)}
                        className="px-4 py-2 border rounded hover:bg-gray-50 text-sm"
                      >Cancel</button>
                      <button
                        onClick={handleConfirmDeleteStage}
                        disabled={deletingStage || ((deleteStageModal.dealCount > 0 || deleteStageModal.taskCount > 0 || deleteStageModal.templateCount > 0) && !deleteStageModal.replacement)}
                        className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 text-sm flex items-center gap-1"
                      >
                        {deletingStage ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                        {deletingStage ? 'Deleting...' : 'Delete Stage'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Task Statuses Tab */}
          {adminTab === 'task-statuses' && (
            <div>
              <h2 className="font-semibold text-lg mb-2" style={{ color: displayPrimary }}>Task Statuses</h2>
              <p className="text-sm mb-4" style={{ color: displaySecondary }}>
                Configure the statuses a task can have. "Complete" statuses count toward progress calculations.
              </p>

              {statusesLoading ? (
                <div className="flex items-center gap-2 py-4">
                  <Loader2 className="w-4 h-4 animate-spin" style={{ color: displayPrimary }} />
                  <span style={{ color: displaySecondary }}>Loading statuses...</span>
                </div>
              ) : (
                <>
                  <div className="border rounded mb-4 divide-y">
                    {statuses.map(status => (
                      <div key={status.id} className="flex items-center gap-3 p-3 hover:bg-gray-50">
                        <div 
                          className="w-4 h-4 rounded-full flex-shrink-0" 
                          style={{ backgroundColor: status.color }} 
                        />
                        <span className="flex-1 font-medium">{status.name}</span>
                        {status.is_complete && (
                          <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded">Counts as Complete</span>
                        )}
                        {status.organization_id ? (
                          <button 
                            onClick={() => deleteStatus(status.id)}
                            className="p-1 hover:bg-red-50 rounded text-red-500"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400">Default</span>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="bg-gray-50 rounded p-4">
                    <h3 className="font-medium mb-3">Add New Status</h3>
                    <div className="flex gap-2 items-end">
                      <div className="flex-1">
                        <label className="block text-xs mb-1">Name</label>
                        <input 
                          value={newStatus.name}
                          onChange={e => setNewStatus(p => ({ ...p, name: e.target.value }))}
                          placeholder="e.g., Blocked"
                          className="w-full border rounded px-3 py-2"
                        />
                      </div>
                      <div>
                        <label className="block text-xs mb-1">Color</label>
                        <input 
                          type="color"
                          value={newStatus.color}
                          onChange={e => setNewStatus(p => ({ ...p, color: e.target.value }))}
                          className="w-12 h-10 border rounded cursor-pointer"
                        />
                      </div>
                      <label className="flex items-center gap-2 pb-2">
                        <input 
                          type="checkbox"
                          checked={newStatus.is_complete}
                          onChange={e => setNewStatus(p => ({ ...p, is_complete: e.target.checked }))}
                          className="rounded"
                        />
                        <span className="text-sm">Complete</span>
                      </label>
                      <button
                        onClick={async () => {
                          if (!newStatus.name.trim()) return;
                          try {
                            await addStatus(newStatus);
                            setNewStatus({ name: '', color: '#6b7280', is_complete: false });
                            showToast('Status added');
                          } catch (err) {
                            showToast(err.message, 'error');
                          }
                        }}
                        className="px-4 py-2 text-white rounded hover:opacity-90 transition-opacity"
                        style={{ backgroundColor: primaryColor }}
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Deal Reasons Tab — admin-editable category lists behind the
              cancellation-reason and at-risk/delay-reason dropdowns on a
              deal. Both share the same underlying table (kind column),
              rendered as two independent flat lists here. */}
          {adminTab === 'deal-reasons' && (
            <div>
              <h2 className="font-semibold text-lg mb-2" style={{ color: displayPrimary }}>Deal Reasons</h2>
              <p className="text-sm mb-4" style={{ color: displaySecondary }}>
                Categories offered when canceling a deal, or when explaining why an active deal has stalled.
              </p>

              {reasonCategoriesLoading ? (
                <div className="flex items-center gap-2 py-4">
                  <Loader2 className="w-4 h-4 animate-spin" style={{ color: displayPrimary }} />
                  <span style={{ color: displaySecondary }}>Loading categories...</span>
                </div>
              ) : (
                [
                  { kind: 'cancellation', title: 'Cancellation Reasons', list: cancellationCategories, placeholder: 'e.g., Asset Quality' },
                  { kind: 'delay', title: 'Delay / At-Risk Reasons', list: delayCategories, placeholder: 'e.g., Waiting on Seller Response' },
                ].map(({ kind, title, list, placeholder }) => (
                  <div key={kind} className="mb-6">
                    <h3 className="font-medium mb-2 text-sm" style={{ color: displayPrimary }}>{title}</h3>
                    <div className="border rounded mb-3 divide-y">
                      {list.length === 0 && (
                        <div className="p-3 text-sm" style={{ color: displaySecondary }}>No categories yet.</div>
                      )}
                      {list.map(cat => (
                        <div key={cat.id} className="flex items-center gap-3 p-3 hover:bg-gray-50">
                          <span className="flex-1 font-medium text-sm">{cat.label}</span>
                          <button
                            onClick={async () => {
                              if (!confirm(`Remove "${cat.label}"? Deals that already used it keep showing it, but it won't be offered for new selections.`)) return;
                              try {
                                await deactivateReasonCategory(cat.id);
                                showToast('Category removed');
                              } catch (err) {
                                showToast(err.message, 'error');
                              }
                            }}
                            className="p-1 hover:bg-red-50 rounded text-red-500"
                            title="Remove"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input
                        value={newReasonLabel[kind]}
                        onChange={e => setNewReasonLabel(p => ({ ...p, [kind]: e.target.value }))}
                        placeholder={placeholder}
                        className="flex-1 border rounded px-3 py-2 text-sm"
                      />
                      <button
                        onClick={async () => {
                          const label = newReasonLabel[kind].trim();
                          if (!label) return;
                          try {
                            await addReasonCategory(kind, label);
                            setNewReasonLabel(p => ({ ...p, [kind]: '' }));
                            showToast('Category added');
                          } catch (err) {
                            showToast(err.message, 'error');
                          }
                        }}
                        className="px-4 py-2 text-white rounded hover:opacity-90 transition-opacity"
                        style={{ backgroundColor: primaryColor }}
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Templates Tab */}
          {adminTab === 'templates' && (
            <div>
              <div className="flex justify-between items-start mb-2">
                <h2 className="font-semibold text-lg" style={{ color: displayPrimary }}>Task Templates</h2>
                <button
                  onClick={async () => {
                    if (!confirm('This will apply category settings from templates to all matching tasks in open deals. Continue?')) return;
                    try {
                      const templatesWithCategories = templates.filter(t => t.category_id);
                      if (templatesWithCategories.length === 0) {
                        showToast('No templates have categories assigned', 'error');
                        return;
                      }
                      let updatedCount = 0;
                      for (const template of templatesWithCategories) {
                        const { data, error } = await supabase
                          .from('tasks')
                          .update({ category_id: template.category_id })
                          .eq('organization_id', organization?.id)
                          .eq('name', template.name)
                          .eq('phase', template.phase)
                          .is('category_id', null)
                          .select('id');
                        if (error) throw error;
                        updatedCount += data?.length || 0;
                      }
                      showToast('Categories applied to ' + updatedCount + ' tasks');
                    } catch (err) {
                      showToast(err.message, 'error');
                    }
                  }}
                  className="px-3 py-1.5 text-sm border rounded hover:bg-gray-50"
                  title="Apply category settings to existing tasks in open deals"
                >
                  Apply Categories to Deals
                </button>
                <button
                  onClick={async () => {
                    if (!confirm('This will add any new template tasks to all open deals (without duplicating existing tasks). Continue?')) return;
                    try {
                      // Get all open deals
                      var dealsRes = await supabase.from('deals').select('id, organization_id').eq('organization_id', organization.id).is('deleted_at', null);
                      if (dealsRes.error) throw dealsRes.error;
                      var openDeals = dealsRes.data || [];
                      var totalAdded = 0;
                      for (var d = 0; d < openDeals.length; d++) {
                        var deal = openDeals[d];
                        // Get existing task names for this deal
                        var tasksRes = await supabase.from('tasks').select('name, phase').eq('deal_id', deal.id);
                        if (tasksRes.error) continue;
                        var existingKeys = (tasksRes.data || []).map(function(t) { return t.name + '|' + t.phase; });
                        // Find templates not yet in this deal
                        var missing = templates.filter(function(tmpl) {
                          return !existingKeys.includes(tmpl.name + '|' + tmpl.phase);
                        });
                        if (missing.length > 0) {
                          var newTasks = missing.map(function(tmpl) {
                            return {
                              deal_id: deal.id,
                              organization_id: deal.organization_id,
                              name: tmpl.name,
                              phase: tmpl.phase,
                              status: 'Not Started',
                              is_doc_request: tmpl.is_doc_request || false,
                              sort_order: tmpl.sort_order || 0,
                              relative_days: tmpl.relative_days || null,
                              base_date: tmpl.base_date || null,
                              category_id: tmpl.category_id || null,
                            };
                          });
                          var insertRes = await supabase.from('tasks').insert(newTasks);
                          if (!insertRes.error) totalAdded += newTasks.length;
                        }
                      }
                      showToast('Added ' + totalAdded + ' tasks across ' + openDeals.length + ' deals');
                    } catch (err) {
                      showToast(err.message, 'error');
                    }
                  }}
                  className="px-3 py-1.5 text-sm border rounded hover:bg-gray-50"
                  title="Add any new template tasks to all open deals (won't duplicate existing tasks)"
                >
                  Sync Templates to Deals
                </button>
              </div>
              <p className="text-sm mb-4" style={{ color: displaySecondary }}>
                These templates are applied to new deals. Click on a template to edit it. Tasks marked as "Document Request" can be requested from sellers.
              </p>

              <div className="max-h-[500px] overflow-y-auto mb-4 border rounded">
                {PHASES.map(phase => {
                  const phaseTasks = templates.filter(t => t.phase === phase.id);
                  if (!phaseTasks.length) return null;
                  return (
                    <div key={phase.id} className="border-b last:border-b-0">
                      <div className="px-3 py-2 bg-gray-50 font-medium text-sm flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: phase.color }} />
                        {phase.name}
                        <span style={{ color: displaySecondary }}>({phaseTasks.length})</span>
                      </div>
                      {phaseTasks.map(task => (
                        editingTemplate?.id === task.id ? (
                          // Editing mode
                          <div key={task.id} className="px-3 py-2 border-t bg-blue-50">
                            <div className="flex gap-2 mb-2">
                              <select 
                                value={editingTemplate.phase}
                                onChange={e => setEditingTemplate(p => ({ ...p, phase: e.target.value }))}
                                className="border rounded px-2 py-1 text-sm"
                              >
                                {PHASES.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                              </select>
                              <input 
                                value={editingTemplate.name}
                                onChange={e => setEditingTemplate(p => ({ ...p, name: e.target.value }))}
                                className="flex-1 border rounded px-2 py-1 text-sm"
                              />
                            </div>
                            <div className="flex gap-4 items-center flex-wrap">
                              <label className="flex items-center gap-2 text-sm">
                                <input 
                                  type="checkbox" 
                                  checked={editingTemplate.is_doc_request}
                                  onChange={e => setEditingTemplate(p => ({ ...p, is_doc_request: e.target.checked }))}
                                  className="rounded"
                                />
                                Doc Request
                              </label>
                              <select
                                value={editingTemplate.category_id || ''}
                                onChange={e => setEditingTemplate(p => ({ ...p, category_id: e.target.value || null }))}
                                className="border rounded px-2 py-1 text-sm"
                              >
                                <option value="">No Folder</option>
                                {categoryOptions.map(opt => (
                                  <option key={opt.id} value={opt.id}>{opt.indent ? '  ' : ''}{opt.label}</option>
                                ))}
                              </select>
                              <div className="flex items-center gap-1">
                                <input 
                                  type="number" 
                                  value={editingTemplate.relative_days}
                                  onChange={e => setEditingTemplate(p => ({ ...p, relative_days: +e.target.value }))}
                                  className="w-16 border rounded px-2 py-1 text-sm"
                                />
                                <span className="text-sm" style={{ color: displaySecondary }}>days from</span>
                                <select 
                                  value={editingTemplate.base_date}
                                  onChange={e => setEditingTemplate(p => ({ ...p, base_date: e.target.value }))}
                                  className="border rounded px-2 py-1 text-sm"
                                >
                                  <option value="created">Created</option>
                                  <option value="loi">LOI</option>
                                  <option value="close">Close</option>
                                </select>
                              </div>
                              <div className="flex gap-2 ml-auto">
                                <button 
                                  onClick={() => setEditingTemplate(null)}
                                  className="px-2 py-1 text-sm border rounded hover:bg-gray-100"
                                >
                                  Cancel
                                </button>
                                <button 
                                  onClick={async () => {
                                    try {
                                      await updateTemplate(editingTemplate.id, {
                                        name: editingTemplate.name,
                                        phase: editingTemplate.phase,
                                        is_doc_request: editingTemplate.is_doc_request,
                                        relative_days: editingTemplate.relative_days,
                                        base_date: editingTemplate.base_date,
                                        category_id: editingTemplate.category_id || null
                                      });
                                      setEditingTemplate(null);
                                      showToast('Template updated');
                                    } catch (err) {
                                      showToast(err.message, 'error');
                                    }
                                  }}
                                  className="px-2 py-1 text-sm text-white rounded hover:opacity-90"
                                  style={{ backgroundColor: primaryColor }}
                                >
                                  Save
                                </button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          // Display mode
                          <div 
                            key={task.id} 
                            className="flex items-center justify-between px-3 py-2 text-sm border-t hover:bg-gray-50 cursor-pointer group"
                            onClick={() => setEditingTemplate({
                              id: task.id,
                              name: task.name,
                              phase: task.phase,
                              is_doc_request: task.is_doc_request,
                              relative_days: task.relative_days || 0,
                              base_date: task.base_date || 'loi',
                              category_id: task.category_id || ''
                            })}
                          >
                            <div className="flex items-center gap-2">
                              {task.is_doc_request && <FileText className="w-3 h-3" style={{ color: displayPrimary }} title="Document Request" />}
                              <span>{task.name}</span>
                              {task.category_id && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-600">
                                  {categoryOptions.find(o => o.id === task.category_id)?.label || 'Folder set'}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs" style={{ color: displaySecondary }}>
                                {task.relative_days >= 0 ? '+' : ''}{task.relative_days || 0}d from {task.base_date || 'loi'}
                              </span>
                              <button 
                                onClick={(e) => { e.stopPropagation(); handleDeleteTemplate(task.id); }} 
                                className="text-red-500 text-xs hover:underline opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        )
                      ))}
                    </div>
                  );
                })}
                {templates.length === 0 && (
                  <div className="p-8 text-center" style={{ color: displaySecondary }}>
                    No templates yet. Add one below.
                  </div>
                )}
              </div>

              <div className="border-t pt-4">
                <h3 className="font-medium mb-3">Add Task Template</h3>
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <select 
                      value={newTask.phase} 
                      onChange={e => setNewTask(p => ({ ...p, phase: e.target.value }))}
                      className="border rounded px-2 py-1.5 text-sm w-48"
                    >
                      <option value="">Select Phase</option>
                      {PHASES.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                    <input 
                      value={newTask.name} 
                      onChange={e => setNewTask(p => ({ ...p, name: e.target.value }))}
                      placeholder="Task name" 
                      className="flex-1 border rounded px-2 py-1.5 text-sm" 
                    />
                  </div>
                  <div className="flex gap-4 items-center flex-wrap">
                    <label className="flex items-center gap-2 text-sm">
                      <input 
                        type="checkbox" 
                        checked={newTask.is_doc_request} 
                        onChange={e => setNewTask(p => ({ ...p, is_doc_request: e.target.checked }))}
                        className="rounded"
                      />
                      Document Request
                    </label>
                    <select
                      value={newTask.category_id || ''}
                      onChange={e => setNewTask(p => ({ ...p, category_id: e.target.value || null }))}
                      className="border rounded px-2 py-1 text-sm"
                    >
                      <option value="">No Folder</option>
                      {categoryOptions.map(opt => (
                        <option key={opt.id} value={opt.id}>{opt.indent ? '  ' : ''}{opt.label}</option>
                      ))}
                    </select>
                    <div className="flex items-center gap-1">
                      <input 
                        type="number" 
                        value={newTask.relative_days} 
                        onChange={e => setNewTask(p => ({ ...p, relative_days: +e.target.value }))}
                        className="w-16 border rounded px-2 py-1 text-sm" 
                      />
                      <span className="text-sm" style={{ color: displaySecondary }}>days from</span>
                      <select 
                        value={newTask.base_date} 
                        onChange={e => setNewTask(p => ({ ...p, base_date: e.target.value }))}
                        className="border rounded px-2 py-1 text-sm"
                      >
                        <option value="created">Deal Created</option>
                        <option value="loi">LOI Date</option>
                        <option value="close">Close Date</option>
                      </select>
                    </div>
                    <button 
                      onClick={handleAddTemplate}
                      className="px-3 py-1.5 text-white rounded text-sm hover:opacity-90 transition-opacity"
                      style={{ backgroundColor: primaryColor }}
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* File Categories Tab */}
          {adminTab === 'file-categories' && (
            <FileCategoriesTab
              organizationId={organization?.id}
              primaryColor={primaryColor}
              secondaryColor={secondaryColor}
              showToast={showToast}
            />
          )}

          {/* Team Tab */}
          {adminTab === 'team' && (() => {
            const q = teamSearch.trim().toLowerCase();
            const filteredTeam = team.filter(m => {
              if (teamRoleFilter !== 'all' && m.role !== teamRoleFilter) return false;
              if (q && !`${m.full_name || ''} ${m.email || ''}`.toLowerCase().includes(q)) return false;
              return true;
            });
            const roleCounts = team.reduce((acc, m) => {
              acc[m.role] = (acc[m.role] || 0) + 1;
              return acc;
            }, {});
            return (
            <div>
              <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                <h2 className="font-semibold text-lg" style={{ color: displayPrimary }}>
                  Team Members <span className="text-sm font-normal" style={{ color: displaySecondary }}>({filteredTeam.length}{filteredTeam.length !== team.length ? ` of ${team.length}` : ''})</span>
                </h2>
                <div className="flex items-center gap-2 flex-wrap">
                  <input
                    type="text"
                    value={teamSearch}
                    onChange={e => setTeamSearch(e.target.value)}
                    placeholder="Search name or email…"
                    className="h-8 text-[13px] rounded-md px-2 focus:outline-none"
                    style={{ border: '1px solid #E5E9F0', backgroundColor: 'white', width: 220 }}
                  />
                  <select
                    value={teamRoleFilter}
                    onChange={e => setTeamRoleFilter(e.target.value)}
                    className="h-8 text-[12px] rounded-md px-2 cursor-pointer focus:outline-none font-medium"
                    style={{ border: '1px solid #E5E9F0', backgroundColor: 'white', color: '#0F172A' }}
                  >
                    <option value="all">All roles ({team.length})</option>
                    {['admin','member','viewer','limited_viewer','integration','board_member','deal_advisor'].map(r =>
                      roleCounts[r] > 0 && <option key={r} value={r}>{r.replace('_',' ')} ({roleCounts[r]})</option>
                    )}
                  </select>
                </div>
              </div>

              {/* Current Team Members */}
              <div className="space-y-1 mb-6">
                {filteredTeam.map(m => {
                  // Format last_sign_in_at as a relative time chip:
                  // "Today" / "Xh ago" / "Xd ago" / dated. "Never" if NULL.
                  const lastLogin = (() => {
                    if (!m.last_sign_in_at) return { label: 'Never', tone: 'idle' };
                    const ts = new Date(m.last_sign_in_at);
                    const ms = Date.now() - ts.getTime();
                    const hours = ms / 3600000;
                    const days = ms / 86400000;
                    let label;
                    if (hours < 1) label = 'Just now';
                    else if (hours < 24) label = `${Math.floor(hours)}h ago`;
                    else if (days < 7) label = `${Math.floor(days)}d ago`;
                    else if (days < 30) label = `${Math.floor(days / 7)}w ago`;
                    else label = ts.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                    let tone = 'fresh';
                    if (days >= 30) tone = 'stale';
                    else if (days >= 7) tone = 'aging';
                    return { label, tone };
                  })();
                  const toneStyle = {
                    fresh:  { bg: '#ECFDF5', fg: '#047857', border: '#A7F3D0' },
                    aging:  { bg: '#FEF3C7', fg: '#A16207', border: '#FDE68A' },
                    stale:  { bg: '#FEE2E2', fg: '#B91C1C', border: '#FECACA' },
                    idle:   { bg: '#F1F5F9', fg: '#64748B', border: '#E5E9F0' },
                  }[lastLogin.tone];
                  return (
                  <div key={m.id} className="flex justify-between items-center px-3 py-1.5 bg-gray-50 rounded gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-[13px] truncate">{m.full_name}</span>
                        <span className="text-[12px] truncate" style={{ color: displaySecondary }}>{m.email}</span>
                        <span
                          className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded flex-shrink-0"
                          style={{ backgroundColor: toneStyle.bg, color: toneStyle.fg, border: `1px solid ${toneStyle.border}`, letterSpacing: '0.04em' }}
                          title={m.last_sign_in_at ? `Last sign-in: ${new Date(m.last_sign_in_at).toLocaleString()}` : 'No recorded sign-in'}
                        >
                          {lastLogin.label}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <select
                        value={m.role}
                        onChange={async (e) => {
                          try {
                            await updateMemberRole(m.id, e.target.value);
                            showToast('Role updated');
                          } catch (err) {
                            showToast(err.message, 'error');
                          }
                        }}
                        className="text-xs px-2 py-1 rounded border"
                      >
                        <option value="member">Member</option>
                        <option value="admin">Admin</option>
                        <option value="viewer">Viewer</option>
                        <option value="limited_viewer">Limited Viewer</option>
                        <option value="integration">Integrations</option>
                        <option value="board_member">Board Member</option>
                        <option value="deal_advisor">Deal Advisor</option>
                      </select>
                      <button
                        onClick={async () => {
                          const neverLoggedIn = !m.last_sign_in_at;
                          const confirmMsg = neverLoggedIn
                            ? `Resend the invite link to ${m.email}?`
                            : `Send password reset email to ${m.email}?`;
                          if (!confirm(confirmMsg)) return;
                          try {
                            await sendPasswordReset(m.email);
                            showToast(neverLoggedIn ? 'Invite link resent' : 'Password reset email sent');
                          } catch (err) {
                            showToast(err.message, 'error');
                          }
                        }}
                        className="text-xs px-2 py-1 border rounded hover:bg-gray-100"
                        title={m.last_sign_in_at ? 'Send password reset' : 'This person hasn\'t set a password yet — send them a fresh link'}
                      >
                        {m.last_sign_in_at ? 'Reset Password' : 'Resend Invite'}
                      </button>
                      {m.role === 'deal_advisor' && (
                        <button
                          onClick={() => setManageAccessFor(m)}
                          className="text-xs px-2 py-1 border rounded hover:bg-gray-100 flex items-center gap-1"
                          title="Manage which deals this advisor can see, in one place"
                        >
                          <Briefcase className="w-3 h-3" />
                          Deal Access
                        </button>
                      )}
                      {(() => {
                        const isLastAdmin = m.role === 'admin' && team.filter(t => t.role === 'admin').length <= 1;
                        return (
                          <button
                            onClick={async () => {
                              if (isLastAdmin) {
                                showToast('Cannot remove the last admin. Promote another member first.', 'error');
                                return;
                              }
                              if (!confirm(`Remove ${m.full_name} from the team?`)) return;
                              try {
                                await removeMember(m.id);
                                showToast('Member removed');
                              } catch (err) {
                                showToast(err.message, 'error');
                              }
                            }}
                            className={`p-1 rounded ${isLastAdmin ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-red-50 text-red-500'}`}
                            title={isLastAdmin ? 'Cannot remove the last admin' : 'Remove from team'}
                            disabled={isLastAdmin}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        );
                      })()}
                    </div>
                  </div>
                  );
                })}
                {filteredTeam.length === 0 && (
                  <div className="p-4 text-center text-sm" style={{ color: displaySecondary }}>
                    {team.length === 0
                      ? 'No team members yet.'
                      : 'No members match the current filter.'}
                  </div>
                )}
              </div>

              {/* Pending Invites */}
              {invites.filter(i => !i.accepted_at).length > 0 && (
                <div className="mb-6">
                  <h3 className="font-medium mb-2 flex items-center gap-2">
                    Pending Invites
                    <span className="text-xs px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full">
                      {invites.filter(i => !i.accepted_at).length}
                    </span>
                  </h3>
                  <div className="space-y-2">
                    {invites.filter(i => !i.accepted_at).map(invite => (
                      <div 
                        key={invite.id} 
                        className="flex justify-between items-center p-3 rounded border bg-yellow-50 border-yellow-200"
                      >
                        <div>
                          <div className="font-medium">{invite.full_name || invite.email}</div>
                          <div className="text-sm" style={{ color: displaySecondary }}>{invite.email}</div>
                          <div className="text-xs mt-1" style={{ color: displaySecondary }}>
                            Invited {new Date(invite.created_at).toLocaleDateString()}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs px-2 py-1 bg-white rounded capitalize border">
                            {invite.role}
                          </span>
                          <button
                            onClick={async () => {
                              setResendingInviteId(invite.id);
                              try {
                                await resendInvite(invite);
                                showToast('Invite resent');
                              } catch (err) {
                                showToast(err.message, 'error');
                              } finally {
                                setResendingInviteId(null);
                              }
                            }}
                            disabled={resendingInviteId === invite.id}
                            className="p-1 hover:bg-blue-50 rounded text-blue-500 disabled:opacity-50"
                            title="Resend invite"
                          >
                            {resendingInviteId === invite.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                          </button>
                          <button
                            onClick={async () => {
                              try {
                                await cancelInvite(invite.id);
                                showToast('Invite cancelled');
                              } catch (err) {
                                showToast(err.message, 'error');
                                }
                              }}
                              className="p-1 hover:bg-red-50 rounded text-red-500"
                              title="Cancel invite"
                            >
                              <X className="w-4 h-4" />
                            </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Invite New Member */}
              <div className="border-t pt-4">
                <h3 className="font-medium mb-2">Invite Team Member</h3>
                <p className="text-sm mb-3" style={{ color: displaySecondary }}>
                  Send an invite - when they sign up with this email, they'll automatically join your team.
                </p>
                <div className="flex gap-2 flex-wrap">
                  <input 
                    value={newMember.name} 
                    onChange={e => setNewMember(p => ({ ...p, name: e.target.value }))}
                    placeholder="Name" 
                    className="flex-1 min-w-[150px] border rounded px-3 py-2 text-sm" 
                  />
                  <input 
                    value={newMember.email} 
                    onChange={e => setNewMember(p => ({ ...p, email: e.target.value }))}
                    placeholder="Email" 
                    className="flex-1 min-w-[200px] border rounded px-3 py-2 text-sm" 
                  />
                  <select 
                    value={newMember.role} 
                    onChange={e => setNewMember(p => ({ ...p, role: e.target.value }))}
                    className="border rounded px-2 py-2 text-sm"
                  >
                    <option value="member">Member</option>
                    <option value="admin">Admin</option>
                    <option value="viewer">Viewer</option>
                    <option value="limited_viewer">Limited Viewer</option>
                    <option value="integration">Integrations</option>
                    <option value="board_member">Board Member</option>
                    <option value="deal_advisor">Deal Advisor</option>
                  </select>
                  <button 
                    onClick={handleInviteMember}
                    className="px-3 py-2 text-white rounded hover:opacity-90 transition-opacity"
                    style={{ backgroundColor: primaryColor }}
                  >
                    <UserPlus className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
            );
          })()}

          {/* Import Deals Tab */}
          {/* LFG Locations Tab */}
          {adminTab === 'locations' && (
            <LfgLocationsTab
              organizationId={organization?.id}
              primaryColor={primaryColor}
              secondaryColor={secondaryColor}
              showToast={showToast}
            />
          )}

          {/* Vendor Folders Tab */}
          {adminTab === 'vendor-folders' && (
            <VendorFoldersTab
              organizationId={organization?.id}
              primaryColor={primaryColor}
              secondaryColor={secondaryColor}
              showToast={showToast}
            />
          )}

          {/* Daily Digest Tab */}
          {adminTab === 'digest' && (
            <DigestSettingsTab
              organizationId={organization?.id}
              primaryColor={primaryColor}
              secondaryColor={secondaryColor}
              showToast={showToast}
            />
          )}

          {adminTab === 'surveys' && (
            <SurveysTab
              organizationId={organization?.id}
              primaryColor={primaryColor}
              showToast={showToast}
            />
          )}

          {adminTab === 'import' && (
            <CsvImportTab 
              organizationId={organization?.id}
              stages={PHASES}
              primaryColor={primaryColor}
              secondaryColor={secondaryColor}
              showToast={showToast}
            />
          )}

          {/* Activity Log Tab */}
          {adminTab === 'activity' && (
            <div className="space-y-4">
              <h2 className="font-semibold text-lg" style={{ color: displayPrimary }}>Activity Log</h2>
              <p className="text-sm" style={{ color: displaySecondary }}>
                Recent changes to deals, tasks, and files in your organization.
              </p>
              
              {auditLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin" style={{ color: displayPrimary }} />
                </div>
              ) : auditLogs.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  No activity recorded yet.
                </div>
              ) : (
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {auditLogs.map(log => (
                    <div key={log.id} className="border rounded-lg p-3 hover:bg-gray-50">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span 
                              className={`text-xs font-medium px-2 py-0.5 rounded ${
                                log.action === 'create' ? 'bg-green-100 text-green-700' :
                                log.action === 'update' ? 'bg-blue-100 text-blue-700' :
                                'bg-red-100 text-red-700'
                              }`}
                            >
                              {log.action.toUpperCase()}
                            </span>
                            <span className="font-medium text-sm">{log.table_name}</span>
                            {log.changed_fields && log.changed_fields.length > 0 && (
                              <span className="text-xs text-gray-500">
                                ({log.changed_fields.join(', ')})
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {log.user_email || 'System'} {'\u2022'} {new Date(log.created_at).toLocaleString()}
                          </div>
                          {log.action === 'update' && log.changed_fields && (
                            <div className="mt-2 text-xs space-y-1">
                              {log.changed_fields.filter(f => f !== 'updated_at').slice(0, 3).map(field => (
                                <div key={field} className="flex gap-2">
                                  <span className="font-medium text-gray-600">{field}:</span>
                                  <span className="text-red-600 line-through truncate max-w-[150px]">
                                    {log.old_data?.[field] !== undefined ? String(log.old_data[field]).substring(0, 30) : '(empty)'}
                                  </span>
                                  <span>â†’</span>
                                  <span className="text-green-600 truncate max-w-[150px]">
                                    {log.new_data?.[field] !== undefined ? String(log.new_data[field]).substring(0, 30) : '(empty)'}
                                  </span>
                                </div>
                              ))}
                              {log.changed_fields.filter(f => f !== 'updated_at').length > 3 && (
                                <div className="text-gray-400">+{log.changed_fields.length - 3} more fields</div>
                              )}
                            </div>
                          )}
                          {log.action === 'create' && log.new_data?.name && (
                            <div className="mt-1 text-xs text-gray-600">
                              Created: <span className="font-medium">{log.new_data.name}</span>
                            </div>
                          )}
                          {log.action === 'delete' && log.old_data?.name && (
                            <div className="mt-1 text-xs text-gray-600">
                              Deleted: <span className="font-medium">{log.old_data.name}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Trash Tab */}
          {adminTab === 'trash' && (
            <TrashTab
              organizationId={organization?.id}
              primaryColor={primaryColor}
              secondaryColor={secondaryColor}
              showToast={showToast}
            />
          )}
          </div>{/* end p-6 content pad */}
        </div>{/* end content card */}
      </div>{/* end two-col flex */}
      {manageAccessFor && (
        <ManageAdvisorAccessModal
          advisor={manageAccessFor}
          organizationId={organization?.id}
          stages={PHASES}
          onClose={() => setManageAccessFor(null)}
          showToast={showToast}
        />
      )}
    </div>
  );
}

// ============================================================================
// MANAGE DEAL ADVISOR ACCESS MODAL
// ============================================================================
// One place to see/toggle every deal an LFG-retained advisor (outside
// counsel, an accounting firm — someone spanning multiple deals over
// time) has access to, instead of visiting each deal's own Access panel
// separately and generating a notification email each time. Purely
// additive: every toggle here is the exact same deal_access insert/delete
// the per-deal Grant/Revoke buttons already use — nothing about how
// access is checked or how a deal_advisor experiences a deal changes.
//
// Scoped by default to Under LOI / On Hold / Closed deals — a retained
// advisor doesn't need to see early prospecting-stage deals, and the full
// org-wide deal list would be a long, mostly-irrelevant scroll. Any deal
// the advisor already has access to stays visible regardless of stage
// (so revoking something granted at an earlier stage is still possible),
// and "Show all deals" reveals everything for the rare exception.
const ADVISOR_ELIGIBLE_STAGES = new Set(['under-loi', 'on-hold', 'closed']);

function ManageAdvisorAccessModal({ advisor, organizationId, stages, onClose, showToast }) {
  const [loadingDeals, setLoadingDeals] = useState(true);
  const [deals, setDeals] = useState([]); // { id, name, stage }
  const [accessRows, setAccessRows] = useState([]); // { id, deal_id }
  const [showAll, setShowAll] = useState(false);
  const [savingId, setSavingId] = useState(null);

  const stageMeta = (stageId) => {
    const found = (stages || []).find(s => s.id === stageId);
    return found || { name: stageId || 'Unknown', color: '#94A3B8' };
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingDeals(true);
      const [dealsRes, accessRes] = await Promise.all([
        supabase.from('deals').select('id, name, stage').eq('organization_id', organizationId).is('deleted_at', null).order('name'),
        supabase.from('deal_access').select('id, deal_id').eq('user_id', advisor.id),
      ]);
      if (cancelled) return;
      setDeals(dealsRes.data || []);
      setAccessRows(accessRes.data || []);
      setLoadingDeals(false);
    })();
    return () => { cancelled = true; };
  }, [advisor.id, organizationId]);

  const grantedDealIds = useMemo(() => new Set(accessRows.map(r => r.deal_id)), [accessRows]);

  const visibleDeals = useMemo(() => {
    return deals.filter(d => showAll || ADVISOR_ELIGIBLE_STAGES.has(d.stage) || grantedDealIds.has(d.id));
  }, [deals, showAll, grantedDealIds]);

  const hiddenCount = deals.length - visibleDeals.length;

  const toggle = async (deal) => {
    setSavingId(deal.id);
    try {
      if (grantedDealIds.has(deal.id)) {
        const row = accessRows.find(r => r.deal_id === deal.id);
        if (row) {
          const { error } = await supabase.from('deal_access').delete().eq('id', row.id);
          if (error) throw error;
          setAccessRows(prev => prev.filter(r => r.id !== row.id));
        }
      } else {
        const { data: { user } } = await supabase.auth.getUser();
        const { data, error } = await supabase
          .from('deal_access')
          .insert({ deal_id: deal.id, user_id: advisor.id, granted_by: user?.id })
          .select('id, deal_id')
          .single();
        if (error) throw error;
        setAccessRows(prev => [...prev, data]);
      }
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-4 py-3 border-b flex items-center justify-between flex-shrink-0">
          <div className="min-w-0">
            <div className="font-semibold text-sm truncate">{advisor.full_name || advisor.email}</div>
            <div className="text-xs text-gray-500">Manage deal access</div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 flex-shrink-0"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-4 py-2 text-[11px] text-gray-500 border-b bg-gray-50 flex-shrink-0">
          Showing deals at Under LOI, On Hold, or Closed
          {hiddenCount > 0 && !showAll ? ` — ${hiddenCount} earlier-stage deal(s) hidden` : ''}.
          {hiddenCount > 0 && (
            <button onClick={() => setShowAll(s => !s)} className="ml-1 text-blue-600 hover:underline">
              {showAll ? 'Hide earlier-stage deals' : 'Show all deals'}
            </button>
          )}
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
          {loadingDeals ? (
            <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
          ) : visibleDeals.length === 0 ? (
            <div className="text-xs text-gray-400 text-center py-8">No deals to show</div>
          ) : visibleDeals.map(d => {
            const meta = stageMeta(d.stage);
            const checked = grantedDealIds.has(d.id);
            return (
              <label key={d.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={savingId === d.id}
                  onChange={() => toggle(d)}
                  className="flex-shrink-0"
                />
                <span className="flex-1 text-[13px] truncate">{d.name}</span>
                <span
                  className="text-[10px] font-medium px-1.5 py-0.5 rounded flex-shrink-0"
                  style={{ backgroundColor: `${meta.color}18`, color: meta.color }}
                >
                  {meta.name}
                </span>
                {savingId === d.id && <Loader2 className="w-3 h-3 animate-spin text-gray-400 flex-shrink-0" />}
              </label>
            );
          })}
        </div>
        <div className="px-4 py-2 border-t text-[11px] text-gray-400 flex-shrink-0">
          Toggling here is silent — no notification email. Use "Send DD request" from within a deal if you want them notified.
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// CSV IMPORT TAB COMPONENT
// ============================================================================
function CsvImportTab({ organizationId, stages, primaryColor, secondaryColor, showToast }) {
  const { displayPrimary, displaySecondary, displayNavy, displayGreen } = useDisplayColors(primaryColor, secondaryColor);
  const [file, setFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [columnMapping, setColumnMapping] = useState({
    parentCompany: '',
    locationName: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    isCrematory: '',
    stage: ''
  });
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [step, setStep] = useState(1); // 1: upload, 2: map columns, 3: preview, 4: results

  const requiredFields = ['parentCompany', 'address', 'city', 'state'];
  const optionalFields = ['locationName', 'zip', 'isCrematory', 'stage'];

  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length === 0) return { headers: [], data: [] };

    // Parse header row
    const headerLine = lines[0];
    const headers = parseCSVLine(headerLine);

    // Parse data rows
    const data = [];
    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      if (values.length === headers.length) {
        const row = {};
        headers.forEach((h, idx) => {
          row[h] = values[idx];
        });
        data.push(row);
      }
    }

    return { headers, data };
  };

  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    
    return result;
  };

  const handleFileUpload = (e) => {
    const uploadedFile = e.target.files?.[0];
    if (!uploadedFile) return;

    setFile(uploadedFile);
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const { headers, data } = parseCSV(text);
      
      setHeaders(headers);
      setCsvData(data);
      
      // Auto-map columns if names match
      const autoMapping = { ...columnMapping };
      const fieldNames = {
        parentCompany: ['parent company', 'parent_company', 'company', 'name', 'funeral home', 'funeral_home', 'seller', 'business', 'deal'],
        locationName: ['location name', 'location_name', 'location', 'branch', 'site'],
        address: ['address', 'street', 'street address', 'street_address'],
        city: ['city', 'town'],
        state: ['state', 'st'],
        zip: ['zip', 'zipcode', 'zip code', 'postal', 'postal code', 'zip_code'],
        isCrematory: ['crematory', 'is crematory', 'is_crematory', 'crem'],
        stage: ['stage', 'status', 'phase']
      };

      headers.forEach(h => {
        const headerLower = h.toLowerCase().trim();
        Object.entries(fieldNames).forEach(([field, aliases]) => {
          if (aliases.includes(headerLower) && !autoMapping[field]) {
            autoMapping[field] = h;
          }
        });
      });

      setColumnMapping(autoMapping);
      setStep(2);
    };
    reader.readAsText(uploadedFile);
  };

  const handleMappingChange = (field, value) => {
    setColumnMapping(prev => ({ ...prev, [field]: value }));
  };

  const isMappingComplete = () => {
    return requiredFields.every(field => columnMapping[field]);
  };

  const getMappedData = () => {
    return csvData.map((row, idx) => {
      const mapped = { _rowNum: idx + 2 }; // +2 for 1-indexed and header row
      Object.entries(columnMapping).forEach(([field, column]) => {
        if (column) {
          mapped[field] = row[column]?.trim() || '';
        }
      });
      
      // Parse crematory field
      const cremValue = mapped.isCrematory?.toLowerCase();
      mapped.isCrematory = ['yes', 'true', '1', 'y', 'x'].includes(cremValue);
      
      // Validate stage
      const stageValue = mapped.stage?.toLowerCase();
      const matchedStage = stages.find(s => 
        s.id.toLowerCase() === stageValue || 
        s.name.toLowerCase() === stageValue
      );
      mapped.stageId = matchedStage?.id || 'prospect';
      mapped.stageName = matchedStage?.name || 'Prospect';
      mapped.stageValid = !!matchedStage || !mapped.stage;
      
      // Validate required fields
      mapped.isValid = mapped.parentCompany && mapped.address && mapped.city && mapped.state;
      
      return mapped;
    });
  };

  // Group mapped data by parent company for preview
  const getGroupedData = () => {
    const mappedData = getMappedData();
    const groups = {};
    
    mappedData.forEach(row => {
      const key = row.parentCompany?.toLowerCase().trim();
      if (!groups[key]) {
        groups[key] = {
          name: row.parentCompany,
          stageId: row.stageId,
          stageName: row.stageName,
          locations: []
        };
      }
      groups[key].locations.push(row);
      // Use the stage from the first row for this company
    });
    
    return Object.values(groups);
  };

  const geocodeAddress = async (address, city, state, zip) => {
    const r = await geocodeAddressShared(address, city, state, zip);
    return r ? { latitude: r.latitude, longitude: r.longitude }
             : { latitude: null, longitude: null };
  };

  const handleImport = async () => {
    setImporting(true);
    const groupedData = getGroupedData();
    const results = { 
      dealsCreated: 0, 
      dealsUpdated: 0,
      locationsAdded: 0, 
      failed: 0, 
      errors: [] 
    };

    for (const group of groupedData) {
      try {
        // Check if deal already exists
        const { data: existingDeals } = await supabase
          .from('deals')
          .select('id, name')
          .eq('organization_id', organizationId)
          .ilike('name', group.name);

        let dealId;
        
        if (existingDeals && existingDeals.length > 0) {
          // Use existing deal
          dealId = existingDeals[0].id;
          results.dealsUpdated++;
        } else {
          // Create new deal - use first location's address as deal address
          const firstLoc = group.locations[0];
          const dealCoords = await geocodeAddress(
            firstLoc.address, firstLoc.city, firstLoc.state, firstLoc.zip
          );
          await new Promise(resolve => setTimeout(resolve, 1100)); // Rate limit

          const { data: newDeal, error: dealError } = await supabase
            .from('deals')
            .insert({
              organization_id: organizationId,
              name: group.name,
              stage: group.stageId,
              address: firstLoc.address,
              city: firstLoc.city,
              state: firstLoc.state?.toUpperCase(),
              zip: firstLoc.zip,
              latitude: dealCoords.latitude,
              longitude: dealCoords.longitude
            })
            .select()
            .single();

          if (dealError) throw dealError;
          dealId = newDeal.id;
          results.dealsCreated++;
        }

        // Add locations for this deal
        for (let i = 0; i < group.locations.length; i++) {
          const loc = group.locations[i];
          
          if (!loc.isValid) {
            results.failed++;
            results.errors.push({ row: loc._rowNum, error: 'Missing required fields' });
            continue;
          }

          try {
            // Geocode location
            const coords = await geocodeAddress(loc.address, loc.city, loc.state, loc.zip);
            await new Promise(resolve => setTimeout(resolve, 1100)); // Rate limit

            // Insert location
            const { error: locError } = await supabase
              .from('deal_locations')
              .insert({
                deal_id: dealId,
                organization_id: organizationId,
                name: loc.locationName || `Location ${i + 1}`,
                address: loc.address,
                city: loc.city,
                state: loc.state?.toUpperCase(),
                zip: loc.zip,
                is_crematory: loc.isCrematory,
                latitude: coords.latitude,
                longitude: coords.longitude
              });

            if (locError) throw locError;
            results.locationsAdded++;
          } catch (err) {
            results.failed++;
            results.errors.push({ row: loc._rowNum, error: err.message });
          }
        }
      } catch (err) {
        results.failed++;
        results.errors.push({ group: group.name, error: err.message });
      }
    }

    setImportResults(results);
    setImporting(false);
    setStep(4);
    
    if (results.dealsCreated > 0 || results.locationsAdded > 0) {
      showToast(`Imported ${results.dealsCreated} new deals, ${results.dealsUpdated} updated, ${results.locationsAdded} locations added`);
    }
  };

  const resetImport = () => {
    setFile(null);
    setCsvData([]);
    setHeaders([]);
    setColumnMapping({
      parentCompany: '',
      locationName: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      isCrematory: '',
      stage: ''
    });
    setImportResults(null);
    setStep(1);
  };

  const mappedData = step >= 3 ? getMappedData() : [];
  const groupedData = step >= 3 ? getGroupedData() : [];
  const validCount = mappedData.filter(r => r.isValid).length;
  const crematoryCount = mappedData.filter(r => r.isCrematory).length;

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold" style={{ color: displayPrimary }}>Import Deals & Locations from CSV</h2>
      
      {/* Step 1: Upload */}
      {step === 1 && (
        <div className="space-y-4">
          <p className="text-sm" style={{ color: displaySecondary }}>
            Upload a CSV file with deals and locations. Rows with the same Parent Company will be grouped as multiple locations under one deal.
          </p>
          
          <div className="border-2 border-dashed rounded-lg p-8 text-center">
            <Upload className="w-12 h-12 mx-auto mb-4" style={{ color: displaySecondary }} />
            <label className="cursor-pointer">
              <span 
                className="px-4 py-2 text-white rounded hover:opacity-90"
                style={{ backgroundColor: primaryColor }}
              >
                Choose CSV File
              </span>
              <input 
                type="file" 
                accept=".csv"
                onChange={handleFileUpload}
                className="hidden"
              />
            </label>
            <p className="text-xs mt-2" style={{ color: displaySecondary }}>or drag and drop</p>
          </div>

          <div className="bg-gray-50 rounded p-4">
            <h4 className="font-medium text-sm mb-2">CSV Format Example:</h4>
            <code className="text-xs block bg-white p-2 rounded border overflow-x-auto whitespace-pre">
Parent Company,Location Name,Address,City,State,ZIP,Is Crematory,Stage
Smith Funeral Home,Main Chapel,123 Main St,Columbus,OH,43215,No,Prospect
Smith Funeral Home,West Branch,789 West Ave,Columbus,OH,43220,Yes,Prospect
Jones Memorial,,456 Oak Ave,Cleveland,OH,44101,No,Pre-LOI
Landmark Funeral Group,Downtown,111 Center St,Toledo,OH,43601,No,Complete
            </code>
            <p className="text-xs mt-2" style={{ color: displaySecondary }}>
              <strong>Required:</strong> Parent Company, Address, City, State<br/>
              <strong>Optional:</strong> Location Name, ZIP, Is Crematory (Yes/No), Stage (defaults to Prospect)
            </p>
          </div>
        </div>
      )}

      {/* Step 2: Map Columns */}
      {step === 2 && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm" style={{ color: displaySecondary }}>
              Map your CSV columns to fields. Found {csvData.length} rows.
            </p>
            <button onClick={resetImport} className="text-sm underline" style={{ color: displaySecondary }}>
              Start Over
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {[...requiredFields, ...optionalFields].map(field => {
              const fieldLabels = {
                parentCompany: 'Parent Company / Deal Name',
                locationName: 'Location Name',
                address: 'Street Address',
                city: 'City',
                state: 'State',
                zip: 'ZIP Code',
                isCrematory: 'Is Crematory',
                stage: 'Stage'
              };
              return (
                <div key={field}>
                  <label className="block text-xs font-medium mb-1" style={{ color: displaySecondary }}>
                    {fieldLabels[field] || field}
                    {requiredFields.includes(field) && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  <select
                    value={columnMapping[field]}
                    onChange={e => handleMappingChange(field, e.target.value)}
                    className="w-full border rounded px-3 py-2 text-sm"
                  >
                    <option value="">-- Select Column --</option>
                    {headers.map(h => (
                      <option key={h} value={h}>{h}</option>
                    ))}
                  </select>
                </div>
              );
            })}
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setStep(3)}
              disabled={!isMappingComplete()}
              className="px-4 py-2 text-white rounded hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: primaryColor }}
            >
              Preview Import
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Preview */}
      {step === 3 && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm" style={{ color: displaySecondary }}>
              Review the data before importing. {validCount} of {mappedData.length} rows are valid.
            </p>
            <button onClick={() => setStep(2)} className="text-sm underline" style={{ color: displaySecondary }}>
              Back to Mapping
            </button>
          </div>

          <div className="border rounded max-h-64 overflow-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Parent Company</th>
                  <th className="px-3 py-2 text-left">Location</th>
                  <th className="px-3 py-2 text-left">Address</th>
                  <th className="px-3 py-2 text-left">City, State</th>
                  <th className="px-3 py-2 text-left">Stage</th>
                  <th className="px-3 py-2 text-left">Crematory</th>
                </tr>
              </thead>
              <tbody>
                {mappedData.slice(0, 50).map((row, idx) => (
                  <tr key={idx} className={row.isValid ? '' : 'bg-red-50'}>
                    <td className="px-3 py-2">
                      {row.isValid ? (
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-red-500" />
                      )}
                    </td>
                    <td className="px-3 py-2 font-medium">{row.parentCompany || <span className="text-red-500">Missing</span>}</td>
                    <td className="px-3 py-2 text-gray-600">{row.locationName || '(default)'}</td>
                    <td className="px-3 py-2">{row.address || <span className="text-red-500">Missing</span>}</td>
                    <td className="px-3 py-2">
                      {row.city || <span className="text-red-500">?</span>}, {row.state || <span className="text-red-500">?</span>} {row.zip}
                    </td>
                    <td className="px-3 py-2">
                      <span 
                        className="text-xs px-2 py-0.5 rounded text-white"
                        style={{ backgroundColor: stages.find(s => s.id === row.stageId)?.color || '#6b7280' }}
                      >
                        {row.stageName}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      {row.isCrematory && <Flame className="w-4 h-4 text-orange-500" />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {mappedData.length > 50 && (
              <p className="text-xs text-center py-2" style={{ color: displaySecondary }}>
                Showing first 50 of {mappedData.length} rows
              </p>
            )}
          </div>

          {/* Summary */}
          <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm">
            <strong>Summary:</strong> {groupedData.length} deal{groupedData.length !== 1 ? 's' : ''} with {mappedData.length} total location{mappedData.length !== 1 ? 's' : ''}
            {crematoryCount > 0 && <span className="ml-2 text-orange-600">({crematoryCount} cremator{crematoryCount !== 1 ? 'ies' : 'y'})</span>}
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleImport}
              disabled={validCount === 0 || importing}
              className="px-4 py-2 text-white rounded hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
              style={{ backgroundColor: primaryColor }}
            >
              {importing && <Loader2 className="w-4 h-4 animate-spin" />}
              {importing ? `Importing... (this may take a while)` : `Import ${groupedData.length} Deals, ${validCount} Locations`}
            </button>
            <button
              onClick={resetImport}
              disabled={importing}
              className="px-4 py-2 border rounded hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
          </div>

          {importing && (
            <p className="text-xs" style={{ color: displaySecondary }}>
              Each location is being geocoded for the map. This takes about 1 second per location to respect rate limits.
            </p>
          )}
        </div>
      )}

      {/* Step 4: Results */}
      {step === 4 && importResults && (
        <div className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded p-4">
            <h3 className="font-medium text-green-800 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5" />
              Import Complete
            </h3>
            <p className="text-sm text-green-700 mt-1">
              {importResults.dealsCreated > 0 && `Created ${importResults.dealsCreated} new deal${importResults.dealsCreated !== 1 ? 's' : ''}. `}
              {importResults.dealsUpdated > 0 && `Updated ${importResults.dealsUpdated} existing deal${importResults.dealsUpdated !== 1 ? 's' : ''}. `}
              {importResults.locationsAdded > 0 && `Added ${importResults.locationsAdded} location${importResults.locationsAdded !== 1 ? 's' : ''}.`}
              {importResults.failed > 0 && ` ${importResults.failed} failed.`}
            </p>
          </div>

          {importResults.errors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded p-4">
              <h4 className="font-medium text-red-800 text-sm mb-2">Errors:</h4>
              <ul className="text-xs text-red-700 space-y-1">
                {importResults.errors.slice(0, 10).map((err, idx) => (
                  <li key={idx}>{err.row ? `Row ${err.row}` : err.group}: {err.error}</li>
                ))}
                {importResults.errors.length > 10 && (
                  <li>...and {importResults.errors.length - 10} more errors</li>
                )}
              </ul>
            </div>
          )}

          <button
            onClick={resetImport}
            className="px-4 py-2 text-white rounded hover:opacity-90"
            style={{ backgroundColor: primaryColor }}
          >
            Import More Deals
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// TRASH TAB COMPONENT
// ============================================================================
function TrashTab({ organizationId, primaryColor, secondaryColor, showToast }) {
  const { displayPrimary, displaySecondary, displayNavy, displayGreen } = useDisplayColors(primaryColor, secondaryColor);
  const { deletedDeals, loading, restoreDeal, permanentlyDeleteDeal, refresh } = useDeletedDeals(organizationId);
  const [deleting, setDeleting] = useState(null);
  const [restoring, setRestoring] = useState(null);

  const handleRestore = async (deal) => {
    setRestoring(deal.id);
    try {
      await restoreDeal(deal.id);
      showToast(`"${deal.name}" restored successfully`);
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setRestoring(null);
    }
  };

  const handlePermanentDelete = async (deal) => {
    if (!confirm(`Permanently delete "${deal.name}"? This will delete all associated tasks and files. This cannot be undone!`)) return;
    
    setDeleting(deal.id);
    try {
      await permanentlyDeleteDeal(deal.id);
      showToast(`"${deal.name}" permanently deleted`);
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getDaysUntilPurge = (deletedAt) => {
    const deleted = new Date(deletedAt);
    const purgeDate = new Date(deleted.getTime() + 30 * 24 * 60 * 60 * 1000);
    const daysLeft = Math.ceil((purgeDate - Date.now()) / (1000 * 60 * 60 * 24));
    return daysLeft;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: displayPrimary }} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold" style={{ color: displayPrimary }}>Trash</h2>
          <p className="text-sm" style={{ color: displaySecondary }}>
            Deleted deals are kept for 30 days before being permanently removed.
          </p>
        </div>
        <button 
          onClick={refresh}
          className="text-sm px-3 py-1 rounded border hover:bg-gray-50"
          style={{ color: displaySecondary }}
        >
          Refresh
        </button>
      </div>

      {deletedDeals.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Trash className="w-12 h-12 mx-auto mb-3" style={{ color: displaySecondary }} />
          <p style={{ color: displaySecondary }}>Trash is empty</p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium" style={{ color: displaySecondary }}>Deal Name</th>
                <th className="px-4 py-3 text-left font-medium" style={{ color: displaySecondary }}>Deleted</th>
                <th className="px-4 py-3 text-left font-medium" style={{ color: displaySecondary }}>Auto-purge</th>
                <th className="px-4 py-3 text-right font-medium" style={{ color: displaySecondary }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {deletedDeals.map(deal => {
                const daysLeft = getDaysUntilPurge(deal.deleted_at);
                return (
                  <tr key={deal.id} className="border-t hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium">{deal.name}</td>
                    <td className="px-4 py-3" style={{ color: displaySecondary }}>
                      {formatDate(deal.deleted_at)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded ${daysLeft <= 7 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                        {daysLeft} days left
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleRestore(deal)}
                          disabled={restoring === deal.id || deleting === deal.id}
                          className="px-3 py-1 text-sm rounded border hover:bg-gray-50 flex items-center gap-1 disabled:opacity-50"
                          style={{ color: displayPrimary }}
                        >
                          {restoring === deal.id ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <RotateCcw className="w-3 h-3" />
                          )}
                          Restore
                        </button>
                        <button
                          onClick={() => handlePermanentDelete(deal)}
                          disabled={restoring === deal.id || deleting === deal.id}
                          className="px-3 py-1 text-sm rounded border border-red-200 text-red-600 hover:bg-red-50 flex items-center gap-1 disabled:opacity-50"
                        >
                          {deleting === deal.id ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <Trash2 className="w-3 h-3" />
                          )}
                          Delete Forever
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// SurveysTab — schema-driven survey editor.
//
// Admins can: rename labels, edit hints, toggle required / hidden, reorder
// questions within a section, reorder sections. Cannot add brand-new
// questions or sections — those still need a code-level migration since
// each question id maps to a typed DB column.
// ============================================================================

// Type families — switching a question's type is only safe within a
// family because the underlying DB column type doesn't change. yes_no /
// yes_no_unsure / yes_no_maybe all map to a boolean column where
// 'unsure' / 'maybe' just store NULL. text and textarea both map to TEXT.
// number, date, select, multi_select, multi_bool each have their own
// column shape and don't have a safe swap target.
const TYPE_FAMILIES = {
  yes_no:         [{ value: 'yes_no', label: 'yes / no' }, { value: 'yes_no_unsure', label: 'yes / no / unsure' }, { value: 'yes_no_maybe', label: 'yes / no / maybe' }],
  yes_no_unsure:  [{ value: 'yes_no', label: 'yes / no' }, { value: 'yes_no_unsure', label: 'yes / no / unsure' }, { value: 'yes_no_maybe', label: 'yes / no / maybe' }],
  yes_no_maybe:   [{ value: 'yes_no', label: 'yes / no' }, { value: 'yes_no_unsure', label: 'yes / no / unsure' }, { value: 'yes_no_maybe', label: 'yes / no / maybe' }],
  text:           [{ value: 'text', label: 'short text' }, { value: 'textarea', label: 'long text' }],
  textarea:       [{ value: 'text', label: 'short text' }, { value: 'textarea', label: 'long text' }],
  number:         [{ value: 'number', label: 'number' }],
  date:           [{ value: 'date', label: 'date' }],
  select:         [{ value: 'select', label: 'single-select dropdown' }],
  multi_select:   [{ value: 'multi_select', label: 'multi-select checkboxes' }],
};
const COMPATIBLE_TYPES_FOR = (t) => TYPE_FAMILIES[t] || [{ value: t, label: t }];

// What values can trigger a follow-up, based on the parent question type.
const FOLLOW_UP_TRIGGER_OPTIONS = (parentType) => {
  if (parentType === 'yes_no_unsure') return ['yes', 'no', 'unsure'];
  if (parentType === 'yes_no_maybe')  return ['yes', 'no', 'maybe'];
  if (parentType === 'yes_no')        return ['yes', 'no'];
  // Falls back to yes/no for anything weird; the renderer just compares
  // the picked value against the parent's state, so a no-match means
  // the follow-up never shows.
  return ['yes', 'no'];
};

function SurveysTab({ organizationId, primaryColor, showToast }) {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState(null);
  const [draft, setDraft] = useState(null);     // schema being edited
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!organizationId) return;
    (async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('survey_definitions')
        .select('key, schema, updated_at')
        .eq('organization_id', organizationId);
      if (!error) setSurveys(data || []);
      setLoading(false);
      if (data?.length) setSelectedKey(data[0].key);
    })();
  }, [organizationId]);

  useEffect(() => {
    const row = surveys.find(s => s.key === selectedKey);
    setDraft(row ? structuredClone(row.schema) : null);
    setDirty(false);
  }, [selectedKey, surveys]);

  const updateDraft = (mutator) => {
    setDraft(prev => {
      const next = structuredClone(prev);
      mutator(next);
      return next;
    });
    setDirty(true);
  };

  const reorderArr = (arr, idx, direction) => {
    const dest = idx + direction;
    if (dest < 0 || dest >= arr.length) return;
    const [item] = arr.splice(idx, 1);
    arr.splice(dest, 0, item);
    // Rewrite order to match new positions
    arr.forEach((x, i) => { x.order = i + 1; });
  };

  const save = async () => {
    if (!draft || !selectedKey) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from('survey_definitions')
        .update({ schema: draft, updated_at: new Date().toISOString() })
        .eq('organization_id', organizationId)
        .eq('key', selectedKey);
      if (error) throw error;
      setSurveys(prev => prev.map(s => s.key === selectedKey ? { ...s, schema: draft } : s));
      setDirty(false);
      showToast?.('Survey saved');
    } catch (e) {
      showToast?.(e.message || 'Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center p-8 text-sm text-gray-400">
        <Loader2 className="w-5 h-5 animate-spin inline mr-1" /> Loading surveys…
      </div>
    );
  }

  if (!surveys.length) {
    return <div className="text-sm text-gray-500 p-4">No survey definitions for this organization yet.</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">Survey</span>
          <select
            value={selectedKey || ''}
            onChange={e => setSelectedKey(e.target.value)}
            className="border rounded px-2 py-1 text-sm"
          >
            {surveys.map(s => (
              <option key={s.key} value={s.key}>{s.schema?.title || s.key}</option>
            ))}
          </select>
        </div>
        <button
          onClick={save}
          disabled={!dirty || saving}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-semibold text-white disabled:opacity-50"
          style={{ backgroundColor: primaryColor || '#1A3665' }}
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          {saving ? 'Saving…' : (dirty ? 'Save changes' : 'Saved')}
        </button>
      </div>

      {draft && (
        <div className="space-y-4">
          {/* Survey-level title + description */}
          <div className="border rounded-lg p-3 bg-white">
            <label className="block text-xs text-gray-500 mb-0.5">Survey title</label>
            <input
              className="w-full border rounded px-2 py-1.5 text-sm"
              value={draft.title || ''}
              onChange={e => updateDraft(d => { d.title = e.target.value; })}
            />
            <label className="block text-xs text-gray-500 mb-0.5 mt-2">Introduction (shown to seller at the top)</label>
            <textarea
              className="w-full border rounded px-2 py-1.5 text-sm"
              rows={2}
              value={draft.description || ''}
              onChange={e => updateDraft(d => { d.description = e.target.value; })}
            />
          </div>

          {/* Sections */}
          {(draft.sections || [])
            .map((sec, sIdx) => ({ sec, sIdx }))
            .sort((a, b) => (a.sec.order || 0) - (b.sec.order || 0))
            .map(({ sec, sIdx }) => (
            <div key={sec.id} className="border rounded-lg bg-white">
              <div className="flex items-center gap-2 p-3 bg-gray-50 border-b">
                <button
                  onClick={() => updateDraft(d => reorderArr(d.sections, sIdx, -1))}
                  className="p-1 rounded hover:bg-gray-200" title="Move up"
                >
                  <ChevronUp className="w-4 h-4" />
                </button>
                <button
                  onClick={() => updateDraft(d => reorderArr(d.sections, sIdx, 1))}
                  className="p-1 rounded hover:bg-gray-200" title="Move down"
                >
                  <ChevronDown className="w-4 h-4" />
                </button>
                <input
                  className="flex-1 bg-transparent text-sm font-semibold border-0 border-b border-transparent focus:border-gray-300 focus:outline-none px-1"
                  value={sec.label}
                  onChange={e => updateDraft(d => { d.sections[sIdx].label = e.target.value; })}
                />
                <button
                  onClick={() => updateDraft(d => { d.sections[sIdx].hidden = !d.sections[sIdx].hidden; })}
                  className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded hover:bg-gray-200"
                  title={sec.hidden ? 'Section is hidden — click to show' : 'Hide section from seller'}
                >
                  {sec.hidden ? <EyeOff className="w-3.5 h-3.5 text-gray-400" /> : <Eye className="w-3.5 h-3.5" />}
                  {sec.hidden ? 'Hidden' : 'Visible'}
                </button>
              </div>

              <div className="p-3 space-y-2">
                {(sec.questions || [])
                  .map((q, qIdx) => ({ q, qIdx }))
                  .sort((a, b) => (a.q.order || 0) - (b.q.order || 0))
                  .map(({ q, qIdx }) => (
                  <div key={q.id} className="border rounded p-2 bg-gray-50">
                    <div className="flex items-start gap-2">
                      <div className="flex flex-col gap-0.5 mt-1">
                        <button
                          onClick={() => updateDraft(d => reorderArr(d.sections[sIdx].questions, qIdx, -1))}
                          className="p-0.5 rounded hover:bg-gray-200" title="Move up"
                        >
                          <ChevronUp className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => updateDraft(d => reorderArr(d.sections[sIdx].questions, qIdx, 1))}
                          className="p-0.5 rounded hover:bg-gray-200" title="Move down"
                        >
                          <ChevronDown className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-[10px] font-mono bg-white border px-1 rounded text-gray-500">{q.id}</span>
                          {/* Type picker — only swaps inside compatible
                              groups (same underlying DB column type).
                              multi_bool questions have sub-fields that
                              are tied to the rendering type, so they're
                              fixed. */}
                          {q.type === 'multi_bool' ? (
                            <span className="text-[10px] uppercase tracking-wider text-gray-400">multi_bool</span>
                          ) : (
                            <select
                              value={q.type}
                              onChange={e => updateDraft(d => { d.sections[sIdx].questions[qIdx].type = e.target.value; })}
                              className="text-[11px] border rounded px-1 py-0.5 bg-white"
                              title="Question type — limited to options compatible with the underlying database column"
                            >
                              {COMPATIBLE_TYPES_FOR(q.type).map(t => (
                                <option key={t.value} value={t.value}>{t.label}</option>
                              ))}
                            </select>
                          )}
                          <label className="ml-auto inline-flex items-center gap-1 text-xs text-gray-600 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={!!q.required}
                              onChange={e => updateDraft(d => { d.sections[sIdx].questions[qIdx].required = e.target.checked; })}
                            />
                            Required
                          </label>
                          <button
                            onClick={() => updateDraft(d => { d.sections[sIdx].questions[qIdx].hidden = !d.sections[sIdx].questions[qIdx].hidden; })}
                            className="text-xs px-1.5 py-0.5 rounded hover:bg-white"
                            title={q.hidden ? 'Hidden — click to show' : 'Hide this question'}
                          >
                            {q.hidden ? <EyeOff className="w-3.5 h-3.5 text-gray-400" /> : <Eye className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                        <input
                          className="w-full border rounded px-2 py-1 text-sm bg-white"
                          value={q.label}
                          onChange={e => updateDraft(d => { d.sections[sIdx].questions[qIdx].label = e.target.value; })}
                          placeholder="Question label"
                        />
                        <input
                          className="w-full border rounded px-2 py-1 text-xs bg-white mt-1 text-gray-600"
                          value={q.hint || ''}
                          onChange={e => updateDraft(d => { d.sections[sIdx].questions[qIdx].hint = e.target.value; })}
                          placeholder="Optional hint shown under the question"
                        />
                        {q.follow_up && (
                          <div className="mt-2 pl-3 border-l-2 border-purple-200">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-[10px] uppercase tracking-wider text-purple-500">Follow-up — when answer is</span>
                              {/* Pick which answer triggers the follow-up.
                                  Options come from the parent question
                                  type so a yes_no_unsure question can
                                  trigger on 'unsure' if desired. */}
                              <select
                                value={q.follow_up.when || 'yes'}
                                onChange={e => updateDraft(d => { d.sections[sIdx].questions[qIdx].follow_up.when = e.target.value; })}
                                className="text-[11px] border rounded px-1 py-0.5 bg-white"
                              >
                                {FOLLOW_UP_TRIGGER_OPTIONS(q.type).map(opt => (
                                  <option key={opt} value={opt}>{opt}</option>
                                ))}
                              </select>
                              <button
                                onClick={() => updateDraft(d => { delete d.sections[sIdx].questions[qIdx].follow_up; })}
                                className="ml-auto text-[10px] text-red-500 hover:text-red-700 inline-flex items-center gap-1"
                                title="Remove follow-up"
                              >
                                <X className="w-3 h-3" /> Remove follow-up
                              </button>
                            </div>
                            <input
                              className="w-full border rounded px-2 py-1 text-sm bg-white"
                              value={q.follow_up.label || ''}
                              onChange={e => updateDraft(d => { d.sections[sIdx].questions[qIdx].follow_up.label = e.target.value; })}
                              placeholder="Follow-up label"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <p className="text-xs text-gray-400 italic">
            You can rename labels, edit hints, reorder, toggle required, hide questions, switch question types within a compatible family (yes/no variants; text ↔ textarea), edit / remove / re-target the conditional follow-ups, and edit the survey intro. Adding brand-new questions or renaming question IDs still requires a code change since each field maps to a typed database column.
          </p>
        </div>
      )}
    </div>
  );
}
